// A "snapshot" is a static, non-interactive visual copy of a route's rendered DOM, used as a transition
// layer. We clone the live subtree (cheap and pixel-faithful for normal DOM) rather than rasterizing, so
// there's no html2canvas-style dependency or async paint. Snapshots are inert (`pointer-events:none`) and
// only ever exist between a gesture's start and its settle — never during SSR/hydration.

/** The canonical inline style of a snapshot layer — also used by the cache to re-normalize a layer it clones
 *  after the controller has written transforms onto it (see snapshot-cache.ts). */
export const LAYER_STYLE =
  'position:absolute;inset:0;overflow:hidden;pointer-events:none;background:var(--background);' +
  'will-change:transform;contain:layout paint;'

/**
 * Clone `source` into a positioned, absolutely-filled layer ready to append over the live outlet.
 * Returns the layer plus `applyScroll`, which must be called AFTER the layer is in the document
 * (scroll offsets don't stick on a detached node).
 */
/**
 * The elements a plain `cloneNode` gets wrong and we have to repair by hand: duplicate `id`/`name` (which would
 * shadow the live page's), blank canvases, and scroll containers (offsets aren't cloned).
 *
 * This is a SELECTOR rather than a walk over every node, and that is the point. Snapshotting used to pair
 * `querySelectorAll('*')` over the source AND the clone and step them in lockstep — on an admin table that's
 * tens of thousands of node visits, on the animation's critical path, to fix up a handful of elements. Matching
 * only the elements that can possibly need repair turns it from O(page) into O(a few). Scroll containers are
 * found by their Tailwind `overflow-*` class, which is how every scrollable box in this app is built; one styled
 * inline would be missed, and would simply snapshot un-scrolled.
 */
const NEEDS_REPAIR = '[id],[name],canvas,[class*="overflow-"]'

export function captureSnapshot(source: HTMLElement): { layer: HTMLElement; applyScroll: () => void } {
  const layer = document.createElement('div')
  layer.setAttribute('data-gt-layer', 'snapshot')
  layer.style.cssText = LAYER_STYLE

  const clone = source.cloneNode(true) as HTMLElement
  clone.style.width = '100%'
  clone.style.height = '100%'

  // The two lists are the same query over identical trees (the clone is a fresh deep copy), so they line up
  // index for index.
  const srcNodes = source.querySelectorAll<HTMLElement>(NEEDS_REPAIR)
  const cloneNodes = clone.querySelectorAll<HTMLElement>(NEEDS_REPAIR)
  const scrolls: Array<{ el: HTMLElement; top: number; left: number }> = []
  for (let i = 0; i < srcNodes.length; i++) {
    const s = srcNodes[i]
    const c = cloneNodes[i]
    if (!s || !c) continue
    c.removeAttribute('id')
    c.removeAttribute('name')
    if (s.scrollTop || s.scrollLeft) scrolls.push({ el: c, top: s.scrollTop, left: s.scrollLeft })
    if (s instanceof HTMLCanvasElement && c instanceof HTMLCanvasElement) {
      // Best-effort: copy the rendered bitmap (a clone starts blank). Fails silently on a tainted canvas.
      try {
        c.getContext('2d')?.drawImage(s, 0, 0)
      } catch {
        /* cross-origin/tainted — leave the clone blank */
      }
    }
  }

  layer.appendChild(clone)
  return {
    layer,
    applyScroll: () => {
      for (const { el, top, left } of scrolls) {
        el.scrollTop = top
        el.scrollLeft = left
      }
    },
  }
}
